// =============================================================================
// Code Input Component
// =============================================================================
// 6-digit verification code input with auto-focus and paste support
// =============================================================================

import { useRef, useState, useEffect, KeyboardEvent, ClipboardEvent } from 'react';
import clsx from 'clsx';

// =============================================================================
// Types
// =============================================================================

export interface CodeInputProps {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  error?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
}

// =============================================================================
// Component
// =============================================================================

export function CodeInput({
  length = 6,
  value,
  onChange,
  onComplete,
  error = false,
  disabled = false,
  autoFocus = true,
}: CodeInputProps) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const hasCalledComplete = useRef(false);

  // Auto-focus first input on mount
  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus]);

  // Reset the completion flag when value changes to incomplete
  useEffect(() => {
    if (value.length < length) {
      hasCalledComplete.current = false;
    }
  }, [value, length]);

  // Handle value change - trigger onComplete when all digits entered (only once)
  useEffect(() => {
    if (value.length === length && onComplete && !hasCalledComplete.current) {
      hasCalledComplete.current = true;
      onComplete(value);
    }
  }, [value, length, onComplete]);

  const handleChange = (index: number, digit: string) => {
    // Only allow digits
    if (digit && !/^\d$/.test(digit)) return;

    // Build new value
    const newValue = value.split('');
    newValue[index] = digit;
    const joinedValue = newValue.join('').slice(0, length);

    onChange(joinedValue);

    // Move to next input if digit entered
    if (digit && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
      setActiveIndex(index + 1);
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    // Handle backspace
    if (e.key === 'Backspace') {
      e.preventDefault();

      if (value[index]) {
        // Clear current digit
        const newValue = value.split('');
        newValue[index] = '';
        onChange(newValue.join(''));
      } else if (index > 0) {
        // Move to previous input and clear it
        const newValue = value.split('');
        newValue[index - 1] = '';
        onChange(newValue.join(''));
        inputRefs.current[index - 1]?.focus();
        setActiveIndex(index - 1);
      }
    }

    // Handle left arrow
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
      setActiveIndex(index - 1);
    }

    // Handle right arrow
    if (e.key === 'ArrowRight' && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
      setActiveIndex(index + 1);
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);

    if (pastedData) {
      onChange(pastedData);

      // Focus the input after the last pasted digit
      const nextIndex = Math.min(pastedData.length, length - 1);
      inputRefs.current[nextIndex]?.focus();
      setActiveIndex(nextIndex);
    }
  };

  const handleFocus = (index: number) => {
    setActiveIndex(index);
    // Select the content when focused
    inputRefs.current[index]?.select();
  };

  return (
    <div className="flex justify-center gap-3">
      {Array.from({ length }).map((_, index) => (
        <input
          key={index}
          ref={(el) => {
            inputRefs.current[index] = el;
          }}
          type="text"
          inputMode="numeric"
          pattern="\d*"
          maxLength={1}
          value={value[index] || ''}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          onFocus={() => handleFocus(index)}
          disabled={disabled}
          className={clsx(
            'w-12 h-14 text-center text-2xl font-semibold rounded-lg border-2 transition-colors duration-200',
            'focus:outline-none focus:ring-0',
            disabled && 'bg-gray-100 cursor-not-allowed',
            error
              ? 'border-red-500 focus:border-red-500 text-red-600'
              : index === activeIndex
              ? 'border-primary-500'
              : 'border-gray-300 focus:border-primary-500'
          )}
          aria-label={`Digit ${index + 1}`}
        />
      ))}
    </div>
  );
}

export default CodeInput;
